import { TRPCErrorBoundary } from '@client/components/_core/error-handling/TRPCErrorBoundary';
import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { Space } from '@client/components/_ui/layout/Space';
import { ReadOnlyBanner } from '@client/components/admin/ReadOnlyBanner';
import { SmartPdsUploadModal } from '@client/components/market-pds/SmartPdsUploadModal';
import { useAdminModulePermissions } from '@client/hooks/admin/useAdminModulePermissions';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import type { RouterInputs, RouterOutputs } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Tag } from 'antd';
import { FileStack, RefreshCw, Sparkles } from 'lucide-react';
import { type Key, useState } from 'react';
import { match } from 'ts-pattern';
import { MarketPdsFilters } from './MarketPdsFilters';
import { MARKET_PDS_STATUS_COLORS } from './status-colors';

export const Route = createFileRoute('/admin-console/platform-content/market-pds')({
    staticData: {
        presentation: 'fullPage',
        breadcrumb: {
            title: 'Market PDS',
        },
        ui: {
            icon: FileStack,
            title: 'Market PDS library',
            subtitle: 'Manage the global PDS document library for comparison',
            widthVariant: 'fullWidth',
            menuDescription: 'Manage market PDS documents',
        },
    },
    component: MarketPdsList,
});

type QueryParams = RouterInputs['admin']['marketPds']['listMarketPds'];
type MarketPdsListData = RouterOutputs['admin']['marketPds']['listMarketPds'];
type MarketPdsRow = MarketPdsListData['rows'][number];
type MarketPdsOrderBy = NonNullable<QueryParams['orderBy']>;

function MarketPdsList() {
    const { trpc, trpcUtils } = useTrpc();
    const navigate = useNavigate();
    const { formatDateTime } = useFormatter();
    const { isReadOnly } = useAdminModulePermissions('comms');
    const [smartUploadOpen, setSmartUploadOpen] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);

    const [input, setInput] = usePathState<QueryParams>('query-params', {
        limit: DEFAULT_PAGE_SIZE,
        orderBy: { id: 'desc' },
        where: {},
    });

    const { data, isPending, error } = trpc.admin.marketPds.listMarketPds.useQuery(input, {
        refetchInterval: (query) => {
            const rows = query.state.data?.rows;
            const hasProcessing = rows?.some((r) => r.status === 'PROCESSING');
            return hasProcessing ? 5000 : false;
        },
    });

    const { data: categories } = trpc.admin.marketPds.listCategories.useQuery();
    const { data: insurers } = trpc.admin.marketPds.listInsurers.useQuery();

    const { mutate: deleteMutate } = trpc.admin.marketPds.deleteMarketPds.useMutation({
        onError: (err) => errorMessage.show(err),
    });

    const { mutate: reprocessMutate } = trpc.admin.marketPds.reprocessMarketPds.useMutation({
        onError: (err) => errorMessage.show(err),
    });

    const handleReprocess = (id: bigint) => {
        const key = 'reprocess';
        message.loading({ content: 'Queuing reprocessing...', key });
        reprocessMutate(
            { id },
            {
                onSuccess: () => {
                    message.success({ content: 'Reprocessing queued', key });
                    trpcUtils.admin.marketPds.listMarketPds.invalidate();
                },
            },
        );
    };

    const { mutate: bulkReprocessMutate, isPending: isBulkReprocessing } =
        trpc.admin.marketPds.bulkReprocessMarketPds.useMutation({
            onError: (err) => errorMessage.show(err),
        });

    const handleBulkReprocess = () => {
        const ids = selectedRowKeys as bigint[];
        message.loading({ content: `Queuing ${ids.length} for reprocessing...`, key: 'bulk-reprocess' });
        bulkReprocessMutate(
            { ids },
            {
                onSuccess: (result) => {
                    message.success({
                        content: `${result.queued} PDS queued for reprocessing`,
                        key: 'bulk-reprocess',
                    });
                    setSelectedRowKeys([]);
                    trpcUtils.admin.marketPds.listMarketPds.invalidate();
                },
            },
        );
    };

    const columnHelper = createColumnHelper<MarketPdsRow, MarketPdsOrderBy>();

    const columns = [
        columnHelper.accessor('name', {
            id: 'name',
            header: 'Name',
            cell: ({ row }) => <span className="font-medium">{row.name}</span>,
            className: 'whitespace-nowrap',
        }),
        columnHelper.accessor('insurerName', {
            id: 'insurerName',
            header: 'Insurer',
            cell: ({ row }) => row.insurerName,
            className: 'whitespace-nowrap',
        }),
        columnHelper.accessor('category', {
            id: 'category',
            header: 'Category',
            cell: ({ row }) => (
                <Tag variant="outlined" color="blue">
                    {row.category}
                </Tag>
            ),
            className: 'whitespace-nowrap',
        }),
        columnHelper.accessor('year', {
            id: 'year',
            header: 'Year',
            cell: ({ row }) => row.year,
            className: 'whitespace-nowrap',
        }),
        columnHelper.accessor('status', {
            id: 'status',
            header: 'Status',
            cell: ({ row }) => (
                <Tag variant="outlined" color={MARKET_PDS_STATUS_COLORS[row.status] ?? 'default'}>
                    {row.status}
                </Tag>
            ),
            className: 'whitespace-nowrap',
        }),
        columnHelper.display({
            id: 'sections',
            header: 'Sections',
            cell: ({ row }) => row._count.Sections,
            className: 'whitespace-nowrap',
        }),
        columnHelper.accessor('processingError', {
            id: 'processingError',
            header: 'Error',
            cell: ({ row }) =>
                row.processingError ? (
                    <span className="max-w-48 truncate text-status-danger" title={row.processingError}>
                        {row.processingError}
                    </span>
                ) : (
                    '-'
                ),
            className: 'whitespace-nowrap',
        }),
        columnHelper.accessor('updatedAt', {
            id: 'updatedAt',
            header: 'Updated',
            cell: ({ row }) => formatDateTime(row.updatedAt),
            className: 'whitespace-nowrap',
        }),
        columnHelper.actions({
            cell: ({ row }) =>
                !isReadOnly ? (
                    <Space size="small" className="flex justify-end">
                        {match(row.status)
                            .with('PROCESSING', () => null)
                            .otherwise(() => (
                                <Button.Icon
                                    icon={<RefreshCw />}
                                    color="cyan"
                                    size="small"
                                    tooltip="Reprocess"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleReprocess(row.id);
                                    }}
                                />
                            ))}
                        <Button.Delete
                            onConfirm={(callback) =>
                                deleteMutate(
                                    { id: row.id },
                                    {
                                        onSuccess() {
                                            callback();
                                            trpcUtils.admin.marketPds.listMarketPds.invalidate();
                                        },
                                        onError(err) {
                                            errorMessage.show(err);
                                        },
                                    },
                                )
                            }
                        />
                    </Space>
                ) : null,
            width: 80,
        }),
    ];

    const tableProps = usePrismaTable<MarketPdsRow, QueryParams['where'], MarketPdsOrderBy>({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: isPending,
        columns,
        rowKey: 'id',
        queryParams: input,
        onParamsChange: setInput,
        rowSelection: {
            enabled: !isReadOnly,
            type: 'checkbox',
            preserveSelectedRowKeys: true,
            selectedRowKeys,
            onChange(keys) {
                setSelectedRowKeys(keys);
            },
            getCheckboxProps: (record) => ({
                disabled: record.status === 'PROCESSING',
            }),
        },
    });

    return (
        <PageController route={Route}>
            <TRPCErrorBoundary error={error}>
                <ReadOnlyBanner module="comms" />
                <TableToolbar
                    filters={
                        <MarketPdsFilters
                            onChange={(where) => setInput((prev) => ({ ...prev, where }))}
                            categories={categories ?? []}
                            insurers={insurers?.map((i) => i.insurerName) ?? []}
                        />
                    }
                    actions={
                        selectedRowKeys.length > 0 ? (
                            <Space size="small">
                                <span className="whitespace-nowrap text-muted-foreground text-sm">
                                    {selectedRowKeys.length} selected
                                </span>
                                <Button
                                    type="primary"
                                    icon={<RefreshCw />}
                                    onClick={handleBulkReprocess}
                                    loading={isBulkReprocessing}
                                >
                                    Reprocess
                                </Button>
                            </Space>
                        ) : !isReadOnly ? (
                            <Space size="small">
                                <Button icon={<Sparkles />} onClick={() => setSmartUploadOpen(true)}>
                                    Smart upload
                                </Button>
                                <Button.Add.Link
                                    to="/admin-console/platform-content/market-pds/add"
                                    variant="primary"
                                    label="New PDS"
                                />
                            </Space>
                        ) : undefined
                    }
                />

                <PrismaTable
                    {...tableProps}
                    scroll={{ x: 'max-content' }}
                    onRow={(record) => ({
                        className: 'cursor-pointer',
                        onClick(e) {
                            navigate(
                                {
                                    to: '/admin-console/platform-content/market-pds/edit/$id',
                                    params: { id: String(record.id) },
                                },
                                e,
                            );
                        },
                    })}
                />
                <Outlet />
                <SmartPdsUploadModal
                    open={smartUploadOpen}
                    onClose={() => setSmartUploadOpen(false)}
                    onSuccess={() => {
                        setSmartUploadOpen(false);
                        trpcUtils.admin.marketPds.listMarketPds.invalidate();
                        trpcUtils.admin.marketPds.listCategories.invalidate();
                        trpcUtils.admin.marketPds.listInsurers.invalidate();
                    }}
                    mode="admin"
                />
            </TRPCErrorBoundary>
        </PageController>
    );
}
